/**
 * PricingModeContext — Phase 3 (Drive-First Pivot).
 *
 * Reads /api/system/config/ once on app mount and exposes the active pricing
 * model, current price, trial/grace days, and block policy to the whole tree.
 *
 * Usage:
 *     const { mode, isDriveFirst, isOvacsTiers, config, loading } = usePricingMode()
 *
 * Components reading from this:
 *     - OnboardingWizard (only shows if drive_first + no Drive + no subscription)
 *     - SubscriptionTab (renders different UI per mode)
 *     - GateModal (different copy per mode)
 *     - LandingPage (different pricing section per mode)
 */

import { createContext, useContext, useEffect, useState } from 'react'
import { systemConfigAPI } from '../services/systemConfig'

const PricingModeContext = createContext({
  loading: true,
  mode: 'drive_first',
  config: null,
  isDriveFirst: true,
  isOvacsTiers: false,
})

export function PricingModeProvider({ children }) {
  const [state, setState] = useState({
    loading: true,
    mode: 'drive_first',
    config: null,
    isDriveFirst: true,
    isOvacsTiers: false,
  })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await systemConfigAPI.get()
        if (cancelled) return
        const payload = res?.data?.data ?? res?.data ?? null
        const mode = payload?.pricing_model || 'drive_first'
        setState({
          loading: false,
          mode,
          config: payload,
          isDriveFirst: mode === 'drive_first',
          isOvacsTiers: mode === 'ovacs_tiers',
        })
      } catch (e) {
        if (cancelled) return
        // Fail-open: assume drive_first defaults
        setState({
          loading: false,
          mode: 'drive_first',
          config: {
            pricing_model: 'drive_first',
            annual_price_usd: '60.00',
            trial_days: 90,
            grace_period_days: 14,
            block_uploads: true,
            block_downloads: false,
            block_viewing: false,
          },
          isDriveFirst: true,
          isOvacsTiers: false,
        })
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  return (
    <PricingModeContext.Provider value={state}>
      {children}
    </PricingModeContext.Provider>
  )
}

export function usePricingMode() {
  return useContext(PricingModeContext)
}

export default PricingModeContext
